import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { getCroppedBlob } from "../lib/img";
import { Loader2, ZoomIn } from "lucide-react";

export const PhotoCropDialog = ({ file, onCancel, onCropped }) => {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 }); setZoom(1);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const save = async () => {
    if (!pixels || !src) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(src, pixels, 512);
      onCropped(blob);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="rounded-3xl" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand-navy)]">Position photo</DialogTitle>
          <DialogDescription>Drag to reposition, pinch or use the slider to zoom.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-64 bg-[var(--brand-navy)]/5 rounded-2xl overflow-hidden">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
          <input type="range" min={1} max={3} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom"
            className="w-full accent-[var(--brand-navy)]" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 rounded-full" onClick={onCancel} data-testid="crop-cancel">Cancel</Button>
          <Button className="flex-1 rounded-full accent-bg text-white hover:opacity-90" onClick={save} disabled={saving} data-testid="crop-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Use photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropDialog;
